import { useState, useEffect, useCallback } from "react";
import { Input, Button } from "@hiya/ui";
import { SignatureUpload } from "./SignatureUpload";
import type { User, UserRole } from "../../types/user";
import iconClose from "../../assets/icon-close.svg";
import iconArrowDown from "../../assets/icon-arrow-down.svg";

type Mode = "add" | "edit";

interface UserSlidePanelProps {
  open: boolean;
  mode: Mode;
  user?: User | null;
  onClose: () => void;
  onSubmit: (data: {
    name: string;
    surname: string;
    role: UserRole;
    email: string;
    signature: File | null;
  }) => void;
}

const ROLE_OPTIONS: { value: UserRole; label: string }[] = [
  { value: "admin", label: "Admin" },
  { value: "doctor", label: "Doctor" },
];

export function UserSlidePanel({ open, mode, user, onClose, onSubmit }: Readonly<UserSlidePanelProps>) {
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [role, setRole] = useState<UserRole | "">("");
  const [email, setEmail] = useState("");
  const [signature, setSignature] = useState<File | null>(null);
  const [signatureRemoved, setSignatureRemoved] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const isEdit = mode === "edit";
  const isDoctor = role === "doctor";
  const emailReadOnly = isEdit;

  const resetForm = useCallback(() => {
    if (isEdit && user) {
      setName(user.name);
      setSurname(user.surname);
      setRole(user.role);
      setEmail(user.email);
      setSignature(null);
      setSignatureRemoved(false);
    } else {
      setName("");
      setSurname("");
      setRole("");
      setEmail("");
      setSignature(null);
      setSignatureRemoved(false);
    }
    setShowDropdown(false);
  }, [isEdit, user]);

  useEffect(() => {
    if (open) resetForm();
  }, [open, resetForm]);

  useEffect(() => {
    function handleEsc(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    if (open) {
      document.addEventListener("keydown", handleEsc);
      return () => document.removeEventListener("keydown", handleEsc);
    }
  }, [open, onClose]);

  const fieldsValid =
    name.trim() !== "" &&
    surname.trim() !== "" &&
    role !== "" &&
    email.trim() !== "" &&
    email.includes("@");

  const signatureOk = isDoctor
    ? signature !== null || (isEdit && user?.signature_url != null && !signatureRemoved)
    : true;

  const hasChanges = isEdit
    ? name !== (user?.name ?? "") ||
      surname !== (user?.surname ?? "") ||
      signature !== null ||
      signatureRemoved
    : true;

  const canSubmit = fieldsValid && signatureOk && hasChanges;

  function handleSubmit() {
    if (!canSubmit) return;
    onSubmit({ name: name.trim(), surname: surname.trim(), role: role as UserRole, email: email.trim(), signature });
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-overlay" onClick={onClose} aria-hidden="true" />
      <div className="w-[480px] h-full bg-bg-primary flex flex-col overflow-y-auto p-5">
        <div className="flex flex-col gap-5 flex-1">
          <div className="flex items-center justify-between h-6">
            <h2 className="text-xl font-semibold text-text-primary">
              {isEdit ? "Edit User" : "New User"}
            </h2>
            <button onClick={onClose} aria-label="Close">
              <img src={iconClose} alt="" width={24} height={24} />
            </button>
          </div>

          <div className="flex flex-col gap-2">
            <Input
              placeholder="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <Input
              placeholder="Surname"
              value={surname}
              onChange={(e) => setSurname(e.target.value)}
            />

            {/* Role dropdown */}
            <div className="relative">
              <button
                type="button"
                onClick={() => !isEdit && setShowDropdown(!showDropdown)}
                disabled={isEdit}
                className={`flex items-center gap-2 h-[51px] w-full rounded-2xl border px-4 bg-bg-primary border-stroke-primary text-left ${
                  isEdit ? "opacity-70 cursor-not-allowed" : ""
                }`}
              >
                <span
                  className={`flex-1 text-base font-medium leading-[1.2] ${
                    role ? "text-text-primary" : "text-text-tertiary"
                  }`}
                >
                  {ROLE_OPTIONS.find((r) => r.value === role)?.label ?? "Role"}
                </span>
                {!isEdit && (
                  <img src={iconArrowDown} alt="" width={20} height={20} className="shrink-0" />
                )}
              </button>
              {showDropdown && (
                <div className="absolute top-full left-0 w-full mt-1 bg-bg-primary border border-stroke-primary rounded-xl shadow-lg z-10 overflow-hidden">
                  {ROLE_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        setRole(opt.value);
                        setShowDropdown(false);
                      }}
                      className="w-full text-left px-4 py-3 text-base font-medium text-text-primary hover:bg-bg-secondary transition-colors"
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <Input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={emailReadOnly}
              className={emailReadOnly ? "opacity-70 cursor-not-allowed" : ""}
            />

            {isDoctor && fieldsValid && (
              <SignatureUpload
                file={signature}
                existingUrl={isEdit && !signatureRemoved ? user?.signature_url : null}
                onChange={(file) => {
                  setSignature(file);
                  if (file === null && user?.signature_url) {
                    setSignatureRemoved(true);
                  } else if (file !== null) {
                    setSignatureRemoved(false);
                  }
                }}
              />
            )}
          </div>
        </div>

        <Button
          disabled={!canSubmit}
          onClick={handleSubmit}
          className={canSubmit ? "" : "!bg-btn-primary-disabled"}
        >
          {isEdit ? "Save" : "Add user"}
        </Button>
      </div>
    </div>
  );
}
